// File: frontend/src/utils/dateUtils.ts

/**
 * Parses a timestamp coming from the backend.
 *
 * The backend serializes LocalDateTime without any offset
 * ("2026-07-19T11:20:11.727"). Those values are UTC — the JVM default timezone
 * is pinned to UTC in BackendApplication — but a bare date-time string is read
 * as LOCAL wall-clock time by the browser, which shifts every timestamp by the
 * viewer's offset. Marking it as UTC lets the browser convert it to whatever
 * timezone the reader is actually in.
 *
 * Values that already carry a zone (trailing "Z" or "+02:00") are passed
 * through untouched, and Date inputs are returned as-is, so this is safe to
 * call on any timestamp regardless of its shape.
 */
export const parseServerDate = (value: string | Date | null | undefined): Date => {
  if (value instanceof Date) return value;
  if (!value) return new Date(NaN);
  const raw = String(value);
  const hasZone = /[zZ]$/.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw);
  return new Date(hasZone ? raw : `${raw}Z`);
};

export const formatDateTime = (date: Date | string): string => {
  // Strings always come from the backend, so they go through parseServerDate:
  // a bare "2026-07-19T11:20:11" would otherwise be read as local wall-clock
  // time. This also covers formatAdminDate and every admin panel column.
  const dateObj = typeof date === "string" ? parseServerDate(date) : date;

  const day = dateObj.getDate().toString().padStart(2, "0");
  const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
  const year = dateObj.getFullYear();
  const hours = dateObj.getHours().toString().padStart(2, "0");
  const minutes = dateObj.getMinutes().toString().padStart(2, "0");

  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

// ──────────────────────────────────────────────────────────────────────────
// Timezone-aware match start time handling.
//
// Contract: a match's `matchDate` ("YYYY-MM-DD") and `matchTime` ("HH:mm[:ss]")
// together describe ONE absolute moment, stored in UTC wall-clock form. The
// admin enters the kickoff in their own browser timezone; we convert to UTC for
// storage, and every viewer sees it converted back to their own local timezone.
// ──────────────────────────────────────────────────────────────────────────

const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * Combine the stored UTC date + time parts into an absolute instant (a Date).
 * `matchDate` may be an ISO date string ("YYYY-MM-DD") or a Date whose UTC
 * year/month/day hold the stored date (e.g. produced by `new Date("2026-06-12")`).
 * Returns null when the date is missing/invalid.
 */
export const matchInstantUtc = (
  matchDate: string | Date | null | undefined,
  matchTime: string | null | undefined
): Date | null => {
  if (!matchDate) return null;

  let year: number;
  let monthIndex: number;
  let day: number;

  if (matchDate instanceof Date) {
    if (isNaN(matchDate.getTime())) return null;
    // The Date carries the stored date as UTC midnight, so read UTC parts
    year = matchDate.getUTCFullYear();
    monthIndex = matchDate.getUTCMonth();
    day = matchDate.getUTCDate();
  } else {
    const [yy, mm, dd] = matchDate.split("-").map(Number);
    if (!yy || !mm || !dd) return null;
    year = yy;
    monthIndex = mm - 1;
    day = dd;
  }

  const parts = (matchTime ?? "").split(":").map(Number);
  const hh = Number.isFinite(parts[0]) ? parts[0] : 0;
  const mi = Number.isFinite(parts[1]) ? parts[1] : 0;
  const ss = Number.isFinite(parts[2]) ? parts[2] : 0;
  return new Date(Date.UTC(year, monthIndex, day, hh, mi, ss));
};

/** Format a match's date in the viewer's local timezone. */
export const formatMatchDateLocal = (
  matchDate: string | Date | null | undefined,
  matchTime: string | null | undefined,
  locale = "tr-TR",
  options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
  }
): string => {
  const dt = matchInstantUtc(matchDate, matchTime);
  return dt ? dt.toLocaleDateString(locale, options) : "—";
};

/** Format a match's kickoff time in the viewer's local timezone (24h). */
export const formatMatchTimeLocal = (
  matchDate: string | Date | null | undefined,
  matchTime: string | null | undefined,
  locale = "tr-TR"
): string => {
  const dt = matchInstantUtc(matchDate, matchTime);
  return dt
    ? dt.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "-";
};

/**
 * Convert admin-entered LOCAL date + time (the admin's browser timezone) into the
 * UTC parts that must be stored. Use this right before sending create/update.
 * Returns { matchDate: "YYYY-MM-DD", matchTime: "HH:mm:ss" } in UTC.
 */
export const localInputToUtcParts = (
  localDate: string,
  localTime: string
): { matchDate: string; matchTime: string } => {
  const [y, m, d] = localDate.split("-").map(Number);
  const [hh, mi] = localTime.split(":").map(Number);
  // Interpret the entered values in the admin's local timezone...
  const local = new Date(y, (m || 1) - 1, d, hh || 0, mi || 0, 0);
  // ...then read the equivalent UTC wall-clock parts to persist
  return {
    matchDate: `${local.getUTCFullYear()}-${pad2(local.getUTCMonth() + 1)}-${pad2(
      local.getUTCDate()
    )}`,
    matchTime: `${pad2(local.getUTCHours())}:${pad2(local.getUTCMinutes())}:00`,
  };
};

/**
 * Inverse of localInputToUtcParts: take stored UTC parts and produce the LOCAL
 * date + time strings used to pre-fill the admin form (date input + HH:mm select).
 * Returns { matchDate: "YYYY-MM-DD", matchTime: "HH:mm" } in the admin's timezone.
 */
export const utcPartsToLocalInput = (
  matchDate: string,
  matchTime: string
): { matchDate: string; matchTime: string } => {
  const dt = matchInstantUtc(matchDate, matchTime);
  if (!dt) return { matchDate: "", matchTime: "" };
  return {
    matchDate: `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`,
    matchTime: `${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`,
  };
};